import { Link } from 'react-router-dom';
import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA } from '../components/Pieces';
import { annualReturns, contactInfo } from '../mock';
import { FileText, Download, Clock, ChevronRight, ScrollText } from 'lucide-react';

const COMPANY = 'Mashmari Consultants Private Limited';

const disclosures = [
  {
    href: '/statutory-disclosures/annual-return',
    title: 'Annual Return',
    body: 'Annual returns filed with the Registrar of Companies in Form MGT-7 / MGT-7A, published under Section 92(3) of the Companies Act, 2013.',
  },
];

export function StatutoryDisclosures() {
  return (
    <Shell>
      <Seo title="Statutory Disclosures"
        description={`Statutory disclosures of ${COMPANY}, including annual returns published under the Companies Act, 2013.`} />
      <PageHero
        kicker="Statutory Disclosures"
        title="Disclosures published under the Companies Act, 2013."
        sub={`Documents ${COMPANY} is required to make available to the public on its website.`}
        breadcrumbs={[{ label: 'Statutory Disclosures' }]}
      />
      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-5" data-testid="statutory-disclosures-list">
          {disclosures.map((d) => (
            <Link key={d.href} to={d.href} data-testid={`disclosure-link-${d.href.split('/').pop()}`}
              className="mm-card p-6 group hover:border-[var(--mm-purple)] transition-colors">
              <div className="w-12 h-12 rounded-full bg-[var(--mm-cream)] text-[var(--mm-purple)] flex items-center justify-center">
                <ScrollText className="w-5 h-5" />
              </div>
              <h2 className="mt-5 font-display text-lg font-bold text-[var(--mm-ink)] flex items-center gap-1.5">
                {d.title}
                <ChevronRight className="w-4 h-4 text-[var(--mm-purple)] transition-transform group-hover:translate-x-1" />
              </h2>
              <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{d.body}</p>
            </Link>
          ))}
        </div>
      </Section>
      <CTA title="Need a document that is not listed here?" primary={{ label: 'Contact us', href: '/contact' }} />
    </Shell>
  );
}

export function AnnualReturn() {
  return (
    <Shell>
      <Seo title="Annual Return"
        description={`Annual returns of ${COMPANY} filed in Form MGT-7 / MGT-7A and published under Section 92(3) of the Companies Act, 2013.`} />
      <PageHero
        kicker="Statutory Disclosures"
        title="Annual Return"
        sub="Published in accordance with Section 92(3) of the Companies Act, 2013 read with Rule 12 of the Companies (Management and Administration) Rules, 2014."
        breadcrumbs={[{ label: 'Statutory Disclosures', href: '/statutory-disclosures' }, { label: 'Annual Return' }]}
      />

      <Section bg="white">
        <div className="grid lg:grid-cols-[1fr_340px] gap-10 lg:gap-14 items-start">
          <div>
            <h2 className="mm-kicker mb-5">Filed annual returns</h2>
            <div className="space-y-4" data-testid="annual-returns-list">
              {annualReturns.map((r) => {
                const available = Boolean(r.fileUrl);
                return (
                  <div key={r.financialYear} data-testid={`annual-return-${r.financialYear}`}
                    className="mm-card p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-5">
                    <div className="w-12 h-12 shrink-0 rounded-full bg-[var(--mm-cream)] text-[var(--mm-purple)] flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg font-bold text-[var(--mm-ink)]">
                        Financial Year {r.financialYear}
                      </h3>
                      <p className="mt-1 text-sm text-[var(--mm-ink)]/70">
                        {r.form}
                        {r.filedOn ? ` · Filed on ${r.filedOn}` : ''}
                      </p>
                      {r.note && <p className="mt-2 text-sm text-[var(--mm-ink)]/60 leading-relaxed">{r.note}</p>}
                    </div>
                    {available ? (
                      <a href={r.fileUrl} download target="_blank" rel="noreferrer"
                        data-testid={`annual-return-download-${r.financialYear}`}
                        className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--mm-purple)] text-white text-sm font-semibold hover:bg-[var(--mm-purple-mid)] transition-colors">
                        <Download className="w-4 h-4" /> Download
                      </a>
                    ) : (
                      <span data-testid={`annual-return-pending-${r.financialYear}`}
                        className="shrink-0 inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[var(--mm-line)] bg-[var(--mm-cream)] text-[var(--mm-ink)]/60 text-sm font-semibold">
                        <Clock className="w-4 h-4" /> Publication pending
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-sm text-[var(--mm-ink)]/60 leading-relaxed max-w-2xl">
              Documents are provided in PDF format exactly as filed with the Registrar of Companies.
              For a certified copy or any clarification, write to{' '}
              <a href={`mailto:${contactInfo.email}`} className="text-[var(--mm-purple)] font-semibold hover:opacity-70">{contactInfo.email}</a>.
            </p>
          </div>

          <aside className="mm-card p-6 bg-[var(--mm-cream)]" data-testid="company-identity-card">
            <h2 className="mm-kicker mb-4">Company identity</h2>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-[var(--mm-ink)]/55 text-xs font-semibold uppercase tracking-wider">Name</dt>
                <dd className="mt-1 text-[var(--mm-ink)] font-semibold">{COMPANY}</dd>
              </div>
              {contactInfo.cin && (
                <div>
                  <dt className="text-[var(--mm-ink)]/55 text-xs font-semibold uppercase tracking-wider">CIN</dt>
                  <dd className="mt-1 text-[var(--mm-ink)] font-semibold">{contactInfo.cin}</dd>
                </div>
              )}
              <div>
                <dt className="text-[var(--mm-ink)]/55 text-xs font-semibold uppercase tracking-wider">Registered office</dt>
                <dd className="mt-1 text-[var(--mm-ink)]/80 leading-relaxed">{contactInfo.registeredAddress}</dd>
              </div>
              <div>
                <dt className="text-[var(--mm-ink)]/55 text-xs font-semibold uppercase tracking-wider">Email</dt>
                <dd className="mt-1">
                  <a href={`mailto:${contactInfo.email}`} className="text-[var(--mm-purple)] font-semibold hover:opacity-70">{contactInfo.email}</a>
                </dd>
              </div>
            </dl>
          </aside>
        </div>
      </Section>

      <CTA title="Looking for programme documentation or field evidence instead?"
        primary={{ label: 'View projects', href: '/projects' }}
        secondary={{ label: 'Contact us', href: '/contact' }} />
    </Shell>
  );
}

export default AnnualReturn;
